// ============================================================================
// todo-client-mod -- TodoGroupManager
//
// 分组管理弹窗组件。
// 展示分组列表 + 编辑表单 + 删除确认。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { useGroups } from '../hooks/use-groups.js';
import { useGroupMutations } from '../hooks/use-group-mutations.js';
import { PlusIcon, EditIcon, TrashIcon } from './icons.js';
import { radius, typography, transition } from '../styles/theme.js';
const ICON_OPTIONS = [
    { id: 'inbox', label: '📥' }, { id: 'work', label: '💼' }, { id: 'personal', label: '👤' },
    { id: 'shopping', label: '🛒' }, { id: 'study', label: '📚' }, { id: 'health', label: '❤' },
    { id: 'travel', label: '✈' }, { id: 'finance', label: '💰' }, { id: 'idea', label: '💡' },
    { id: 'star', label: '★' }, { id: 'folder', label: '📁' },
];
const COLOR_PRESETS = ['#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#6b7280'];
export function TodoGroupManager({ onClose, onGroupsChange }) {
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    const { groups, loading, refresh } = useGroups();
    const { creating, updating, deleting, createGroup, updateGroup, deleteGroup } = useGroupMutations();
    const [editingGroup, setEditingGroup] = React.useState(null);
    const [isCreating, setIsCreating] = React.useState(false);
    const [deleteConfirm, setDeleteConfirm] = React.useState(null);
    const [formName, setFormName] = React.useState('');
    const [formDescription, setFormDescription] = React.useState('');
    const [formIcon, setFormIcon] = React.useState('folder');
    const [formColor, setFormColor] = React.useState('#6366f1');
    const resetForm = () => {
        setFormName('');
        setFormDescription('');
        setFormIcon('folder');
        setFormColor('#6366f1');
        setEditingGroup(null);
        setIsCreating(false);
    };
    const handleEdit = (group) => {
        setEditingGroup(group);
        setIsCreating(false);
        setFormName(group.name);
        setFormDescription(group.description ?? '');
        setFormIcon(group.icon);
        setFormColor(group.color ?? '#6366f1');
    };
    const handleCreate = () => {
        setIsCreating(true);
        setEditingGroup(null);
        setFormName('');
        setFormDescription('');
        setFormIcon('folder');
        setFormColor('#6366f1');
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formName.trim())
            return;
        const params = { name: formName.trim(), description: formDescription.trim() || undefined, icon: formIcon, color: formColor };
        let result = null;
        if (editingGroup) {
            result = await updateGroup(editingGroup._id, params);
        }
        else {
            result = await createGroup(params);
        }
        if (result) {
            resetForm();
            refresh();
            onGroupsChange?.();
        }
    };
    const handleDeleteConfirm = async () => {
        if (!deleteConfirm)
            return;
        const success = await deleteGroup(deleteConfirm._id);
        if (success) {
            setDeleteConfirm(null);
            refresh();
            onGroupsChange?.();
        }
    };
    const isFormOpen = isCreating || editingGroup;
    const plusIconNode = React.createElement(PlusIcon);
    const inputStyle = {
        padding: '8px 12px', border: `1px solid ${theme.border.default}`, borderRadius: radius.md,
        fontSize: typography.fontSize.base, outline: 'none',
        backgroundColor: theme.bg.secondary, color: theme.text.primary,
        transition: `border-color ${transition.fast}, box-shadow ${transition.fast}`,
    };
    return React.createElement('div', {
        style: {
            position: 'fixed', inset: 0, backgroundColor: theme.bg.overlay,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: isMobile ? '12px' : '20px',
        },
        onClick: onClose,
    }, React.createElement('div', {
        style: {
            backgroundColor: theme.bg.elevated, borderRadius: radius.xl,
            width: isMobile ? '100%' : '640px', maxWidth: '100%', maxHeight: '85vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative',
            boxShadow: theme.shadow.xl,
            animation: 'todo-scale-in 0.2s ease forwards',
        },
        onClick: (e) => e.stopPropagation(),
    }, 
    // 头部
    React.createElement('div', {
        style: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${theme.border.divider}`,
        },
    }, React.createElement('h3', { style: { margin: 0, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, color: theme.text.primary } }, '管理分组'), React.createElement('button', {
        onClick: onClose, style: {
            background: 'none', border: 'none', fontSize: '22px', color: theme.text.tertiary,
            cursor: 'pointer', padding: '2px 6px', borderRadius: radius.sm,
            transition: `background-color ${transition.fast}, color ${transition.fast}`,
        },
    }, '×')), 
    // 内容
    React.createElement('div', { style: { display: 'flex', overflow: 'hidden', flex: 1, flexDirection: isMobile ? 'column' : 'row' } }, 
    // 左侧：分组列表
    React.createElement('div', {
        style: {
            width: isMobile ? '100%' : '260px', borderRight: isMobile ? 'none' : `1px solid ${theme.border.divider}`,
            borderBottom: isMobile ? `1px solid ${theme.border.divider}` : 'none',
            overflowY: 'auto', padding: '12px', maxHeight: isMobile ? '200px' : undefined,
        },
    }, React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' } }, React.createElement('span', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: theme.text.tertiary, textTransform: 'uppercase' } }, '分组列表'), React.createElement('button', { onClick: handleCreate, style: {
            display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', border: 'none',
            backgroundColor: theme.interaction.selected, color: theme.status.primary,
            borderRadius: radius.sm, fontSize: typography.fontSize.sm, cursor: 'pointer',
            transition: `background-color ${transition.fast}`,
        } }, plusIconNode, React.createElement('span', null, '新建'))), loading && React.createElement('div', { style: { fontSize: typography.fontSize.sm, color: theme.text.tertiary, padding: '12px' } }, '加载中...'), !loading && groups.map(group => React.createElement('div', {
        key: group._id, style: {
            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', borderRadius: radius.md,
            cursor: 'pointer', backgroundColor: editingGroup?._id === group._id ? theme.interaction.selected : 'transparent',
            transition: `background-color ${transition.fast}`,
        },
    }, React.createElement('span', { style: { display: 'flex', alignItems: 'center', fontSize: typography.fontSize.base, color: group.color ?? theme.text.tertiary } }, ICON_OPTIONS.find(i => i.id === group.icon)?.label ?? '📁'), React.createElement('div', { style: { flex: 1, minWidth: 0 } }, React.createElement('span', { style: { display: 'block', fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, group.name), React.createElement('span', { style: { fontSize: typography.fontSize.xs, color: theme.text.tertiary } }, `${group.todoCount} 条待办`)), React.createElement('div', { style: { display: 'flex', gap: '4px' } }, React.createElement('button', {
        onClick: () => handleEdit(group),
        style: {
            padding: '4px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
            color: group.isDefault ? theme.text.disabled : theme.text.tertiary, borderRadius: radius.sm,
            transition: `color ${transition.fast}, background-color ${transition.fast}`,
        },
        title: '编辑', disabled: group.isDefault,
    }, React.createElement(EditIcon)), React.createElement('button', {
        onClick: () => setDeleteConfirm(group),
        style: {
            padding: '4px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
            color: group.isDefault ? theme.text.disabled : theme.status.danger, borderRadius: radius.sm,
            transition: `color ${transition.fast}, background-color ${transition.fast}`,
        },
        title: group.isDefault ? '默认分组不可删除' : '删除', disabled: group.isDefault,
    }, React.createElement(TrashIcon)))))), 
    // 右侧：编辑表单
    React.createElement('div', { style: { flex: 1, padding: '16px', overflowY: 'auto' } }, isFormOpen
        ? React.createElement('form', { onSubmit: handleSubmit, style: { display: 'flex', flexDirection: 'column', gap: '12px' } }, React.createElement('h4', { style: { margin: 0, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.medium, color: theme.text.primary } }, editingGroup ? '编辑分组' : '新建分组'), React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '名称 *'), React.createElement('input', { type: 'text', value: formName, onChange: e => setFormName(e.target.value), placeholder: '如：工作、学习、生活', style: inputStyle, autoFocus: true }), React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '描述'), React.createElement('input', { type: 'text', value: formDescription, onChange: e => setFormDescription(e.target.value), placeholder: '可选描述', style: inputStyle }), React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '图标'), React.createElement('div', { style: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(6, 1fr)', gap: '6px' } }, ...ICON_OPTIONS.map(icon => React.createElement('button', {
            key: icon.id, type: 'button', onClick: () => setFormIcon(icon.id), style: {
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px',
                border: `1px solid ${formIcon === icon.id ? theme.status.primary : theme.border.default}`,
                borderRadius: radius.md, backgroundColor: formIcon === icon.id ? theme.interaction.selected : theme.bg.secondary,
                fontSize: typography.fontSize.lg, cursor: 'pointer',
                transition: `background-color ${transition.fast}, border-color ${transition.fast}`,
            },
        }, icon.label))), React.createElement('label', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '颜色'), React.createElement('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap' } }, ...COLOR_PRESETS.map(color => React.createElement('button', {
            key: color, type: 'button', onClick: () => setFormColor(color), style: {
                width: '28px', height: '28px', borderRadius: '50%',
                border: formColor === color ? `2px solid ${theme.text.primary}` : '2px solid transparent',
                backgroundColor: color, cursor: 'pointer',
                transition: `border-color ${transition.fast}, transform ${transition.fast}`,
                transform: formColor === color ? 'scale(1.1)' : 'scale(1)',
            },
        }))), React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' } }, React.createElement('button', { type: 'button', onClick: resetForm, style: {
                padding: '8px 16px', border: `1px solid ${theme.border.default}`, borderRadius: radius.md,
                backgroundColor: theme.bg.secondary, fontSize: typography.fontSize.base, color: theme.text.secondary,
                cursor: 'pointer', transition: `background-color ${transition.fast}`,
            } }, '取消'), React.createElement('button', {
            type: 'submit', disabled: creating || updating || !formName.trim(), style: {
                padding: '8px 16px', border: 'none', borderRadius: radius.md,
                backgroundColor: theme.status.primary, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                color: theme.text.inverse, cursor: 'pointer',
                opacity: (creating || updating || !formName.trim()) ? 0.6 : 1,
                transition: `background-color ${transition.fast}, opacity ${transition.fast}`,
            },
        }, creating || updating ? '保存中...' : (editingGroup ? '更新' : '创建'))))
        : React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' } }, React.createElement('p', { style: { color: theme.text.tertiary, textAlign: 'center', margin: 0 } }, '新建分组或选择左侧分组进行编辑')))), 
    // 删除确认
    deleteConfirm && React.createElement('div', {
        style: {
            position: 'absolute', inset: 0, backgroundColor: theme.bg.overlay,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        },
    }, React.createElement('div', {
        style: {
            backgroundColor: theme.bg.elevated, borderRadius: radius.xl, padding: '24px',
            width: '360px', maxWidth: '90%', boxShadow: theme.shadow.xl,
            animation: 'todo-scale-in 0.2s ease forwards',
        },
    }, React.createElement('h4', { style: { margin: '0 0 8px 0', fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: theme.text.primary } }, '确认删除分组'), React.createElement('p', { style: { margin: '0 0 20px 0', fontSize: typography.fontSize.base, color: theme.text.secondary, lineHeight: typography.lineHeight.relaxed } }, `确定要删除分组「${deleteConfirm.name}」吗？该分组下的 ${deleteConfirm.todoCount} 条待办将迁移到默认分组。`), React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: '8px' } }, React.createElement('button', { onClick: () => setDeleteConfirm(null), disabled: deleting, style: {
            padding: '8px 16px', border: `1px solid ${theme.border.default}`, borderRadius: radius.md,
            backgroundColor: theme.bg.secondary, fontSize: typography.fontSize.base, color: theme.text.secondary, cursor: 'pointer',
        } }, '取消'), React.createElement('button', { onClick: handleDeleteConfirm, disabled: deleting, style: {
            padding: '8px 16px', border: 'none', borderRadius: radius.md,
            backgroundColor: theme.status.danger, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
            color: '#fff', cursor: 'pointer', opacity: deleting ? 0.6 : 1,
        } }, deleting ? '删除中...' : '确认删除'))))));
}
//# sourceMappingURL=todo-group-manager.js.map